import { OFREPWebProvider } from '@openfeature/ofrep-web-provider';
import { NOOP_PROVIDER, OpenFeature } from '@openfeature/web-sdk';
import { DEFAULT_REGION, EnvironmentEnum } from '../config/region';
import { getItem } from '../utils/storage';

type FeatureFlagsSetupResult =
  | { ok: true; enabled: boolean; baseUrl?: string }
  | { ok: false; enabled: boolean; baseUrl?: string; error: unknown };

let lastProviderRegion: string | null = null;
let lastProviderOk = false;
let providerInitInFlight: { region: string; promise: Promise<FeatureFlagsSetupResult> } | null =
  null;

/** Clears provider cache — for unit tests only. */
export function resetFeatureFlagsProviderCache(): void {
  lastProviderRegion = null;
  lastProviderOk = false;
  providerInitInFlight = null;
}

function getEnvironmentSegment(): EnvironmentEnum {
  const envName = process.env.REACT_APP_ENV_NAME;
  if (envName === EnvironmentEnum.PROD) return EnvironmentEnum.PROD;
  if (envName === EnvironmentEnum.STAGE) return EnvironmentEnum.STAGE;
  return EnvironmentEnum.DEV;
}

function getCurrentRegion(): string {
  const stored = getItem('selectedRegion');
  return typeof stored === 'string' && stored ? stored : DEFAULT_REGION;
}

function buildFeatureFlagsBaseUrl(region: string): string {
  return `https://api.${region}.${getEnvironmentSegment()}.cosafe.com/feature-flags/v1`;
}

function isFeatureFlagsEnabled(): boolean {
  return process.env.REACT_APP_FEATURE_FLAGS_ENABLED === 'true';
}

/**
 * Binds the global OpenFeature client to the OFREP provider, or to
 * {@link NOOP_PROVIDER} when flags are disabled. Safe to fire-and-forget:
 * OFREP init can hang on network and must not block first paint.
 *
 * Prefer {@link ensureFeatureFlagsProvider}: rebinding tears down the
 * provider's flag cache, so synchronous reads return defaults until the new
 * provider is READY.
 */
export async function setupFeatureFlags(): Promise<FeatureFlagsSetupResult> {
  if (!isFeatureFlagsEnabled()) {
    OpenFeature.setProvider(NOOP_PROVIDER);
    lastProviderRegion = getCurrentRegion();
    lastProviderOk = true;
    return { ok: true, enabled: false };
  }

  const region = getCurrentRegion();
  const baseUrl = buildFeatureFlagsBaseUrl(region);

  const token = process.env.REACT_APP_FEATURE_FLAGS_CLIENT_TOKEN?.trim();
  const headers: [string, string][] = [];
  if (token) {
    headers.push(['Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`]);
  }

  try {
    await OpenFeature.setProviderAndWait(new OFREPWebProvider({ baseUrl, headers }));
    lastProviderRegion = region;
    lastProviderOk = true;
    return { ok: true, enabled: true, baseUrl };
  } catch (error) {
    console.error('Feature flags initialization failed', { baseUrl, error });
    lastProviderRegion = region;
    lastProviderOk = false;
    return { ok: false, enabled: true, baseUrl, error };
  }
}

/**
 * Idempotent provider init keyed by the current region. Skips
 * {@link OpenFeature.setProviderAndWait} when the current region is already
 * bound, and serializes concurrent callers onto one in-flight init.
 *
 * Avoids tearing down the provider on same-region auth changes — rebinding
 * opens a NOT_READY window where synchronous flag reads return defaults.
 */
export async function ensureFeatureFlagsProvider(): Promise<FeatureFlagsSetupResult> {
  const region = getCurrentRegion();
  if (lastProviderRegion === region && lastProviderOk) {
    return isFeatureFlagsEnabled()
      ? { ok: true, enabled: true, baseUrl: buildFeatureFlagsBaseUrl(region) }
      : { ok: true, enabled: false };
  }

  // Reuse the in-flight init only when it targets the same region; a region
  // change mid-init (startup/login) instead chains a rebind behind it, so two
  // setProviderAndWait calls never interleave and the later region wins the
  // cache regardless of which network call resolves first.
  if (providerInitInFlight?.region === region) {
    return providerInitInFlight.promise;
  }
  const previous = providerInitInFlight?.promise;
  const promise = (async () => {
    if (previous) await previous.catch(() => undefined);
    return setupFeatureFlags();
  })().finally(() => {
    if (providerInitInFlight?.promise === promise) {
      providerInitInFlight = null;
    }
  });
  providerInitInFlight = { region, promise };
  return promise;
}
